'use client';

import Link from 'next/link';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { ArrowRight, HelpCircle, LifeBuoy, Mail, SendHorizontal } from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import { HeroBanner } from '@/components/ui/HeroBanner';
import { SectionsBlock } from '@/components/cms/SectionRenderer';
import { getPage, type HeroBannerSettings, type Page } from '@/lib/cms';
import { qaApi, type QaQuestion } from '@/lib/api';
import { useAuthStore } from '@/lib/auth-store';
import { resourceHubApi, type ResourceFaq } from '@/lib/resource-hub-api';

export default function FaqPage() {
  const { isAuthenticated } = useAuthStore();
  const [page, setPage] = useState<Page | null>(null);
  const [faqs, setFaqs] = useState<ResourceFaq[]>([]);
  const [myQuestions, setMyQuestions] = useState<QaQuestion[]>([]);
  const [questionInput, setQuestionInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingMine, setLoadingMine] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    getPage('faq').then(setPage).catch(() => setPage(null));
    resourceHubApi.faqs().then((res) => setFaqs(res.data.data ?? [])).catch(() => setFaqs([]));
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setMyQuestions([]);
      return;
    }
    void loadMyQuestions();
  }, [isAuthenticated]);

  async function loadMyQuestions() {
    setLoadingMine(true);
    try {
      const response = await qaApi.myQuestions();
      const payload = response.data?.data;
      const list = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
      setMyQuestions(list);
    } catch {
      setMyQuestions([]);
    } finally {
      setLoadingMine(false);
    }
  }

  async function handleSubmitQuestion(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!questionInput.trim() || submitting) return;

    setSubmitting(true);
    setFeedback(null);
    try {
      await resourceHubApi.submitQuestion({ question: questionInput.trim() });
      setQuestionInput('');
      setFeedback('Question submitted successfully. We will notify your registered email when answered.');
      await loadMyQuestions();
    } catch (error: any) {
      setFeedback(error?.response?.data?.message || 'Failed to submit your question. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const heroSection = page?.sections?.find((section) => section.type === 'hero_banner');
  const title = page?.title || 'Q&A';
  const description =
    page?.content ||
    'Find quick guidance for setup, ticketing, payouts, and day-of operations. Ask your own question and get answers by email.';

  const displayedFaqs = useMemo(() => {
    if (!faqs.length) return [];
    return faqs.map((item) => ({ question: item.question, answer: item.answer }));
  }, [faqs]);

  return (
    <MainLayout>
      <div className="pb-20 space-y-10">
        {heroSection ? (
          <HeroBanner data={(heroSection.settings ?? {}) as HeroBannerSettings} />
        ) : (
          <section className="rounded-3xl border border-landing-border bg-gradient-to-br from-soot via-moss to-soot p-8 text-text-inverse sm:p-12">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-text-inverse/80">Q&A</p>
            <h1 className="mt-3 max-w-3xl text-3xl font-bold leading-tight sm:text-4xl">{title}</h1>
            <p className="mt-4 max-w-2xl text-sm text-text-inverse/85 sm:text-base">{description}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                href="/contact"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-plaster px-5 py-3 text-sm font-semibold text-soot"
              >
                Contact support
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                href="/how-it-works"
                className="inline-flex items-center justify-center rounded-xl border border-white/35 px-5 py-3 text-sm font-semibold text-text-inverse"
              >
                Read setup guide
              </Link>
            </div>
          </section>
        )}

        {page?.sections && page.sections.length > 0 && <SectionsBlock sections={page.sections} />}

        <section className="grid gap-4 lg:grid-cols-[1.3fr_1fr]">
          <article className="rounded-2xl border border-landing-border bg-bg p-6">
            <div className="flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-landing-primary" />
              <h2 className="text-lg font-semibold text-landing-text">Frequently asked questions</h2>
            </div>
            <div className="mt-4 space-y-3">
              {displayedFaqs.length === 0 && (
                <div className="rounded-xl border border-landing-border bg-landing-bg/35 p-4 text-sm text-landing-muted">
                  No FAQs published yet.
                </div>
              )}
              {displayedFaqs.map((item) => (
                <details key={item.question} className="group rounded-xl border border-landing-border bg-landing-bg/35 p-4">
                  <summary className="cursor-pointer list-none pr-3 text-sm font-semibold text-landing-text marker:content-none">
                    <span className="inline-flex w-full items-start justify-between gap-3">
                      <span>{item.question}</span>
                      <span className="text-landing-primary transition-transform group-open:rotate-45">+</span>
                    </span>
                  </summary>
                  <p className="mt-2 text-sm leading-relaxed text-landing-muted">{item.answer}</p>
                </details>
              ))}
            </div>
          </article>

          <aside className="space-y-4">
            <article className="rounded-2xl border border-landing-border bg-bg p-6">
              <div className="inline-flex rounded-lg bg-landing-bg p-2 text-landing-primary">
                <SendHorizontal className="h-4 w-4" />
              </div>
              <h2 className="mt-3 text-lg font-semibold text-landing-text">Ask your own question</h2>
              <p className="mt-2 text-sm text-landing-muted">
                Submit your question here. Once our team answers, we send it to your registered email.
              </p>

              {!isAuthenticated ? (
                <div className="mt-4 rounded-xl border border-landing-border bg-landing-bg/40 p-4 text-sm text-landing-muted">
                  Please sign in to submit and track your questions.
                  <Link href="/login" className="ml-1 font-semibold text-landing-primary">
                    Sign in
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmitQuestion} className="mt-4 space-y-3">
                  <textarea
                    value={questionInput}
                    onChange={(event) => setQuestionInput(event.target.value)}
                    placeholder="Type your question..."
                    rows={4}
                    className="w-full rounded-xl border border-landing-border bg-landing-bg/30 p-3 text-sm text-landing-text outline-none focus:border-landing-primary/60"
                  />
                  <button
                    type="submit"
                    disabled={submitting || !questionInput.trim()}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-soot px-4 py-2.5 text-sm font-semibold text-text-inverse disabled:opacity-60"
                  >
                    {submitting ? 'Submitting...' : 'Submit question'}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </form>
              )}

              {feedback && (
                <p className="mt-3 text-xs text-landing-muted">{feedback}</p>
              )}
            </article>

            <article className="rounded-2xl border border-landing-border bg-bg p-6">
              <div className="inline-flex rounded-lg bg-landing-bg p-2 text-landing-primary">
                <LifeBuoy className="h-4 w-4" />
              </div>
              <h2 className="mt-3 text-lg font-semibold text-landing-text">My submitted questions</h2>

              {!isAuthenticated ? (
                <p className="mt-2 text-sm text-landing-muted">Sign in to view your question status and answers.</p>
              ) : loadingMine ? (
                <p className="mt-2 text-sm text-landing-muted">Loading your questions...</p>
              ) : myQuestions.length === 0 ? (
                <p className="mt-2 text-sm text-landing-muted">No questions submitted yet.</p>
              ) : (
                <div className="mt-3 space-y-2">
                  {myQuestions.map((question) => (
                    <div key={question.id} className="rounded-lg border border-landing-border bg-landing-bg/35 p-3">
                      <p className="text-sm font-medium text-landing-text">{question.question}</p>
                      <p className="mt-1 text-xs uppercase tracking-wide text-landing-muted">Status: {question.status}</p>
                      {question.answer && (
                        <p className="mt-2 text-xs text-landing-muted">
                          <span className="font-semibold text-landing-text">Answer:</span> {question.answer}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </article>

            <article className="rounded-2xl border border-landing-border bg-bg p-6">
              <div className="inline-flex rounded-lg bg-landing-bg p-2 text-landing-primary">
                <Mail className="h-4 w-4" />
              </div>
              <h2 className="mt-3 text-lg font-semibold text-landing-text">Best next reads</h2>
              <div className="mt-3 space-y-2 text-sm">
                <Link href="/articles" className="block rounded-lg border border-landing-border bg-landing-bg/40 px-3 py-2 text-landing-text">
                  Articles and playbooks
                </Link>
                <Link href="/guides/organizers#videos" className="block rounded-lg border border-landing-border bg-landing-bg/40 px-3 py-2 text-landing-text">
                  Event Guide videos
                </Link>
                <Link href="/success-stories" className="block rounded-lg border border-landing-border bg-landing-bg/40 px-3 py-2 text-landing-text">
                  Success stories
                </Link>
              </div>
            </article>
          </aside>
        </section>
      </div>
    </MainLayout>
  );
}
